import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmt, fmtDate, todayISO, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, Plus, Trash2, Edit3, FileDown, Wallet, ChevronRight, RefreshCw, Save, X, ArrowLeft } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Employees List Page
// ─────────────────────────────────────────────────────────────────────────────
export function EmployeesFullPage() {
  const { auth }   = useAuth();
  const isAdmin    = auth?.user?.role === "admin";
  const navigate   = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form, setForm]           = useState({ name:"", role:"", phone:"", wage_type:"monthly", monthly_salary:"", daily_wage:"", opening_balance:"", active:true });
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  // Salary payment
  const [payForm, setPayForm]     = useState({ date: todayISO(), employee_id:"", amount:"", description:"Salary paid", holder_id:"" });
  const [holders,  setHolders]    = useState([]);
  const [showPay,  setShowPay]    = useState(false);
  const [savingPay, setSavingPay] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, h] = await Promise.all([api.get("/employees"), api.get("/cash-holders")]);
      setEmployees(e.data || []);
      setHolders(h.data || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name:"", role:"", phone:"", wage_type:"monthly", monthly_salary:"", daily_wage:"", opening_balance:"", active:true });
    setShowForm(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ name: emp.name, role: emp.role||"", phone: emp.phone||"",
              wage_type: emp.wage_type||"monthly",
              monthly_salary: String(emp.monthly_salary||""),
              daily_wage: String(emp.daily_wage||""),
              opening_balance: String(emp.opening_balance||""),
              active: emp.active !== false });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form,
        monthly_salary: parseFloat(form.monthly_salary||0),
        daily_wage: parseFloat(form.daily_wage||0),
        opening_balance: parseFloat(form.opening_balance||0) };
      if (editing) {
        await api.patch(`/employees/${editing.id}`, payload);
      } else {
        await api.post("/employees", payload);
      }
      setShowForm(false); setEditing(null); await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try { await api.delete(`/employees/${id}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    setSavingPay(true);
    try {
      await api.post("/employees/salary-payment", {
        ...payForm, amount: parseFloat(payForm.amount)
      });
      setShowPay(false);
      setPayForm({ date: todayISO(), employee_id:"", amount:"", description:"Salary paid", holder_id:"" });
    } catch (err) { setError(formatApiError(err)); }
    finally { setSavingPay(false); }
  };

  return (
    <div className="px-6 py-6 max-w-5xl" data-testid="employees-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
            <Users size={11} /> Staff
          </div>
          <h1 className="font-black text-3xl tracking-tight">Employees</h1>
          <p className="text-sm text-gray-500 mt-1">Staff management with salary ledger</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 mt-2">
            <button onClick={() => setShowPay(!showPay)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50"
              data-testid="record-payment-btn">
              <Wallet size={14} /> Record Payment
            </button>
            <button onClick={openAdd}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700"
              data-testid="add-employee-btn">
              <Plus size={14} /> Add Employee
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <form onSubmit={save} className="mb-5 bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h3 className="font-bold text-sm text-gray-800">{editing ? "Edit Employee" : "Add Employee"}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="block text-xs text-gray-500 mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} required placeholder="Full name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="emp-name" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Role</label>
              <input value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))} placeholder="Driver / Loader…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="10-digit number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Wage Type</label>
              <select value={form.wage_type} onChange={e => setForm(f=>({...f,wage_type:e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="emp-wage-type">
                <option value="monthly">Monthly</option>
                <option value="daily">Daily</option>
              </select></div>
            {form.wage_type === "monthly" && (
              <div><label className="block text-xs text-gray-500 mb-1">Monthly Salary ₹</label>
                <input type="number" min="0" step="0.01" value={form.monthly_salary} onChange={e => setForm(f=>({...f,monthly_salary:e.target.value}))} placeholder="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="emp-monthly-salary" /></div>
            )}
            {form.wage_type === "daily" && (
              <div><label className="block text-xs text-gray-500 mb-1">Daily Wage ₹</label>
                <input type="number" min="0" step="0.01" value={form.daily_wage} onChange={e => setForm(f=>({...f,daily_wage:e.target.value}))} placeholder="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            )}
            <div><label className="block text-xs text-gray-500 mb-1">Opening Balance ₹ (advance owed)</label>
              <input type="number" step="0.01" value={form.opening_balance} onChange={e => setForm(f=>({...f,opening_balance:e.target.value}))} placeholder="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="bg-gray-900 text-white rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-50" data-testid="save-employee-btn">
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="text-sm text-gray-500 px-3">Cancel</button>
          </div>
        </form>
      )}

      {/* Salary payment form */}
      {showPay && (
        <form onSubmit={recordPayment} className="mb-5 bg-white border border-gray-200 rounded-xl p-5 space-y-3">
          <h3 className="font-bold text-sm text-gray-800">Record Salary / Advance Payment</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="block text-xs text-gray-500 mb-1">Date *</label>
              <input type="date" value={payForm.date} onChange={e => setPayForm(f=>({...f,date:e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Employee *</label>
              <select value={payForm.employee_id} onChange={e => setPayForm(f=>({...f,employee_id:e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="pay-employee">
                <option value="">Select employee</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select></div>
            <div><label className="block text-xs text-gray-500 mb-1">Amount ₹ *</label>
              <input type="number" step="0.01" min="0" value={payForm.amount} onChange={e => setPayForm(f=>({...f,amount:e.target.value}))} required placeholder="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="pay-amount" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Cash Holder</label>
              <select value={payForm.holder_id} onChange={e => setPayForm(f=>({...f,holder_id:e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">None</option>
                {holders.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select></div>
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">Description</label>
            <input value={payForm.description} onChange={e => setPayForm(f=>({...f,description:e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
          <div className="flex gap-2">
            <button type="submit" disabled={savingPay} className="bg-gray-900 text-white rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-50" data-testid="save-payment-btn">
              {savingPay ? "Saving…" : "Record Payment"}
            </button>
            <button type="button" onClick={() => setShowPay(false)} className="text-sm text-gray-500 px-3">Cancel</button>
          </div>
        </form>
      )}

      {/* Employee cards */}
      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Loading employees…</div>
      ) : employees.length === 0 ? (
        <div className="py-12 text-center bg-white border border-gray-200 rounded-xl">
          <Users size={28} className="mx-auto mb-3 text-gray-300" />
          <div className="text-sm text-gray-500 font-medium">No employees added yet</div>
          {isAdmin && <div className="text-xs text-gray-400 mt-1">Click "Add Employee" to get started</div>}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="employees-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["Name","Role","Phone","Wage","Status","Ledger","Actions"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, i) => (
                  <tr key={emp.id} className={`border-b border-gray-50 ${i%2===0?"":"bg-gray-50/30"}`} data-testid={`employee-row-${emp.id}`}>
                    <td className="px-4 py-3 font-semibold text-gray-800">{emp.name}</td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{emp.role || "—"}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{emp.phone || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {emp.wage_type === "daily"
                        ? <span>₹{fmt(emp.daily_wage)}/day</span>
                        : <span>₹{fmt(emp.monthly_salary)}/mo</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${emp.active !== false ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {emp.active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => navigate(`/employees/${emp.id}/ledger`)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold" data-testid={`view-ledger-${emp.id}`}>
                        View Ledger <ChevronRight size={12} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEdit(emp)} className="p-1 text-gray-400 hover:text-gray-700 rounded">
                            <Edit3 size={13} />
                          </button>
                          <button onClick={() => del(emp.id)} className="p-1 text-gray-300 hover:text-red-500 rounded">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Employee Ledger Page
// ─────────────────────────────────────────────────────────────────────────────
export function EmployeeLedgerPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/employees/${employeeId}/ledger`);
      setData(r.data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const downloadPDF = () => {
    const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
    const stored = localStorage.getItem("lpg_company_auth");
    const token = stored ? JSON.parse(stored)?.token : null;
    if (!token) return;
    const url = `${API_BASE}/employees/${employeeId}/ledger/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `salary_${data?.employee?.name || "ledger"}.pdf`;
        a.click();
      })
      .catch(() => setError("PDF download failed"));
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-gray-400">Loading ledger…</div>;
  if (error)   return <div className="px-6 py-6 text-red-600 text-sm">{error}</div>;
  if (!data)   return null;

  const emp = data.employee;

  return (
    <div className="px-6 py-6 max-w-4xl" data-testid="employee-ledger-page">
      <button onClick={() => navigate("/employees")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-5">
        <ArrowLeft size={14} /> Back to Employees
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Staff</div>
          <h1 className="font-black text-2xl tracking-tight">{emp.name}</h1>
          <div className="text-sm text-gray-500 mt-0.5">{emp.role || "—"} · {emp.phone || "—"}</div>
          <div className="text-sm text-gray-600 mt-1">
            {emp.wage_type === "daily" ? `₹${fmt(emp.daily_wage)}/day` : `₹${fmt(emp.monthly_salary)}/month`}
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="text-right">
            <div className="text-xs text-gray-400 mb-1">Pending Salary</div>
            <div className={`font-black text-2xl ${data.closing_balance >= 0 ? "text-amber-700" : "text-green-700"}`}>
              ₹{fmt(Math.abs(data.closing_balance))}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{data.closing_balance >= 0 ? "owed to employee" : "advance given"}</div>
          </div>
          <button onClick={downloadPDF} className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-semibold" data-testid="download-pdf-btn">
            <FileDown size={14} /> PDF
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-xl font-black text-gray-900">₹{fmt(data.total_earned)}</div>
          <div className="text-xs text-gray-400 mt-1">Total Earned</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-xl font-black text-green-700">₹{fmt(data.total_paid)}</div>
          <div className="text-xs text-gray-400 mt-1">Total Paid</div>
        </div>
        <div className={`border rounded-xl p-4 text-center ${data.closing_balance > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"}`}>
          <div className={`text-xl font-black ${data.closing_balance > 0 ? "text-amber-700" : "text-green-700"}`}>
            ₹{fmt(Math.abs(data.closing_balance))}
          </div>
          <div className="text-xs text-gray-400 mt-1">Pending</div>
        </div>
      </div>

      {/* Ledger table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Salary Ledger</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="emp-ledger-table">
            <thead>
              <tr className="border-b border-gray-100">
                {["Date","Description","Earned (Cr)","Paid (Dr)","Balance"].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.rows || []).length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No entries yet. Mark attendance and record payments to see ledger.</td></tr>
              ) : (data.rows || []).map((r, i) => (
                <tr key={i} className={`border-b border-gray-50 ${i%2===0?"":"bg-gray-50/40"}`}>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{r.date ? fmtDate(r.date) : "Opening"}</td>
                  <td className="px-4 py-2.5 text-gray-700 text-xs">{r.description}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-green-700">{r.credit > 0 ? `₹${fmt(r.credit)}` : ""}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600">{r.debit > 0 ? `₹${fmt(r.debit)}` : ""}</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-bold ${r.balance >= 0 ? "text-amber-700" : "text-green-700"}`}>
                    ₹{fmt(Math.abs(r.balance))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default EmployeesFullPage;
